import { buildSystemPrompt } from './persona'
import { searchTranscripts } from './transcript-store'
import { queryVectorDb } from './vector-db'
import type { TranscriptChunk, PipelineInfo } from './types'
import { logger } from './logger'

export interface ContextPipelineResult {
  personaPrompt: string
  transcriptContext: string
  transcriptResults: { chunk: TranscriptChunk; score: number }[]
  vectorContext: string
  combinedSystemPrompt: string
  threadAnchor: string
  pipelineInfo: PipelineInfo
}

export type PipelinePhase = 'persona' | 'transcripts' | 'vector-db' | 'complete' | 'error'

export interface PipelineProgress {
  phase: PipelinePhase
  message: string
  error?: string
}

type ProgressCallback = (progress: PipelineProgress) => void

// Theological keywords — if user query matches, it's on-topic
const THEOLOGICAL_TERMS = new Set([
  'god', 'allah', 'jesus', 'muhammad', 'quran', 'bible', 'islam', 'muslim',
  'christian', 'trinity', 'tawhid', 'prophet', 'scripture', 'religion',
  'faith', 'believe', 'pray', 'worship', 'sin', 'heaven', 'hell', 'soul',
  'salvation', 'jewish', 'hindu', 'atheist', 'agnostic', 'creator',
  'torah', 'gospel', 'hadith', 'sunnah', 'sharia', 'jihad', 'halal',
  'haram', 'monotheism', 'polytheism', 'apologist', 'debate', 'theology',
  'spiritual', 'afterlife', 'resurrection', 'crucifixion', 'messiah',
  'miracle', 'scientific', 'evolution', 'creation', 'moral', 'proof',
  'evidence', 'logic', 'reason', 'philosophy', 'existence', 'purpose',
  'meaning', 'truth', 'revelation', 'angel', 'devil', 'satan',
  'prophecy', 'prayer', 'fasting', 'zakat', 'hajj', 'pilgrimage',
  'moses', 'abraham', 'noah', 'adam', 'david', 'solomon', 'mary',
  'paul', 'peter', 'gospel', 'psalm', 'verse', 'chapter',
  'suffering', 'evil', 'test', 'trial', 'punishment', 'forgiveness', 'mercy',
  'justice', 'free', 'will', 'predestination', 'fate', 'destiny',
  'buddha', 'buddhism', 'karma', 'reincarnation', 'veda', 'vedic',
  'sect', 'denomination', 'catholic', 'protestant', 'orthodox', 'sunni', 'shia',
  'imam', 'caliph', 'sahabah', 'companion', 'ayah', 'surah', 'ruh', 'nafs',
  'fitrah', 'shirk', 'kufr', 'salah', 'dua', 'dhikr', 'tawbah',
  'hypocrite', 'munafiq', 'mushrik', 'believer', 'unbeliever',
  'atheism', 'theism', 'deism', 'pantheism', 'agnosticism',
  'biblical', 'koran', 'scriptures', 'testament', 'tanakh', 'talmud',
  'abrogation', 'mansukh', 'nasikh', 'ijma', 'qiyas', 'tafsir',
  'wudhu', 'ghusl', 'niqab', 'hijab', 'burqa', 'veil',
  'jannah', 'jahannam', 'sirat', 'mizan', 'qiyamah',
  'dajjal', 'yajuj', 'majuj', 'mahdi', 'isa', 'ibrahim', 'musa',
  'nuh', 'yusuf', 'ayyub', 'yunus', 'yusuf', 'sulaiman', 'dawud',
  'zakariyya', 'yahya', 'maryam', 'hawa', 'iblis', 'shaytan',
  'jibreel', 'mikail', 'israfil', 'azrael', 'munkar', 'nakeer',
  'law', 'attraction', 'cosmic', 'universe', 'consciousness',
  'atheist', 'atheism', 'skeptic', 'secular', 'humanist', 'rationalist',
  'mythology', 'pagan', 'paganism', 'idol', 'idolatry', 'statue',
  'conversion', 'revert', 'convert', 'blasphemy', 'apostasy',
  'madinah', 'makkah', 'kaaba', 'qibla', 'masjid', 'mosque',
  'ramadan', 'eid', 'muharram', 'ashura', 'mawlid', 'isra', 'miraj',
  'nubuwwah', 'risalah', 'wahy', 'ilham', 'kashf', 'karamat',
  'marriage', 'polygamy', 'divorce', 'inheritance', 'witness',
  'dress', 'code', 'modesty', 'awrah', 'mahram', 'non', 'mahram',
  'jizya', 'kharaj', 'zakat', 'sadaqah', 'waqf', 'riba', 'interest',
  'contract', 'marriage', 'nikaah', 'talaq', 'iddah', 'mahr',
  'halal', 'haram', 'makruh', 'mandub', 'mubah', 'wajib', 'fard',
  'bidah', 'innovation', 'ijtihad', 'taqlid', 'madhhab',
  'christianity', 'judaism', 'hinduism', 'buddhism', 'sikhism',
  'confucianism', 'taoism', 'animism', 'shamanism',
  'trinitarian', 'unitarian', 'arianism', 'modalist', 'adoptionist',
  'gnostic', 'gnosticism', 'docetism', 'monophysite', 'nestorian',
  'ecumenical', 'council', 'nicaea', 'constantinople', 'chalcedon',
  'ephesus', 'hippo', 'carthage', 'augustine', 'athanasius', 'arius',
  'constantine', 'theodosius', 'justinian', 'pope', 'patriarch',
  'bishop', 'priest', 'pastor', 'reverend', 'father', 'monk', 'nun',
  'missionary', 'evangelist', 'apostle', 'disciple', 'martyr', 'saint',
  'covenant', 'circumcision', 'baptism', 'eucharist', 'communion',
  'confession', 'penance', 'absolution', 'indulgence', 'purgatory',
  'limbo', 'transubstantiation', 'consubstantiation',
  'incarnation', 'virgin', 'birth', 'immaculate', 'conception',
  'assumption', 'ascension', 'second', 'coming', 'rapture',
  'tribulation', 'millennium', 'apocalypse', 'armageddon',
  'satanism', 'occult', 'witchcraft', 'sorcery', 'magic',
  'superstition', 'cult', 'heresy', 'schism', 'orthodoxy',
  'fundamentalism', 'liberalism', 'modernism', 'progressive',
  'conservative', 'traditional', 'reform', 'orthodox',
  'interfaith', 'dialogue', 'ecumenism', 'pluralism',
  'tolerance', 'diversity', 'inclusivism', 'exclusivism',
  'universalism', 'annihilationism', 'conditional', 'immortality',
  'sheol', 'hades', 'gehenna', 'tartarus', 'abaddon', 'abyss',
  'lucifer', 'beelzebub', 'leviathan', 'asmodeus', 'legion',
  'demon', 'possession', 'exorcism', 'angel', 'archangel',
  'cherubim', 'seraphim', 'ophanim', 'throne', 'dominion',
  'principality', 'power', 'virtue', 'guardian',
])

// Topics the AI will NOT engage with (checked BEFORE theological terms — keep narrow)
const OFF_TOPIC_PATTERNS = [
  /^what(?:'s| is) your (favorite|age|location|hobby|job|height|weight|color)/i,
  /^(can|could|will|would) you (write|create|make|build|help|tell me a story|sing|draw|paint)/i,
  /^tell me a (joke|recipe|poem|song|riddle)/i,
  /^(how (do|can) i|how to) (code|program|install|setup|use|fix|troubleshoot)/i,
  /weather|forecast|temperature|rain|snow|storm/i,
  /movie|tv.?show|episode|netflix|youtube (video|channel)/i,
  /sport|game|football|soccer|basketball|cricket|tennis|golf|hockey|baseball/i,
  /music|song|album|singer|band|playlist|spotify/i,
  /fashion|clothes|brand|shoes|outfit|style/i,
  /recipe|cook|bake|ingredient|kitchen|food|drink|restaurant/i,
  /stock|invest|crypto|bitcoin|nft|price|market|trading/i,
]

function isTheologicalQuery(query: string): boolean {
  const lower = query.toLowerCase()

  // Check for off-topic patterns first
  for (const pattern of OFF_TOPIC_PATTERNS) {
    if (pattern.test(query)) return false
  }

  // Tokenize and check for theological terms
  const tokens = lower.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/)
  const matchedTerms = tokens.filter(t => THEOLOGICAL_TERMS.has(t))

  // If more than 50% of non-stopword tokens are theological, it's on-topic
  const stopwords = new Set(['the', 'a', 'an', 'is', 'are', 'was', 'were', 'be', 'been',
    'have', 'has', 'had', 'do', 'does', 'did', 'will', 'would', 'can', 'could',
    'shall', 'should', 'may', 'might', 'must', 'of', 'in', 'on', 'at', 'to',
    'for', 'with', 'by', 'from', 'up', 'about', 'into', 'over', 'after',
    'all', 'each', 'every', 'both', 'few', 'more', 'most', 'other', 'some',
    'such', 'no', 'nor', 'not', 'only', 'own', 'same', 'so', 'than', 'too',
    'very', 'just', 'because', 'as', 'until', 'while', 'that', 'this', 'these',
    'those', 'it', 'its', 'what', 'which', 'who', 'whom', 'how', 'why',
    'when', 'where', 'i', 'me', 'my', 'we', 'us', 'our', 'you', 'your',
    'he', 'him', 'his', 'she', 'her', 'they', 'them', 'their',
  ])
  const contentTokens = tokens.filter(t => !stopwords.has(t) && t.length > 1)

  if (contentTokens.length === 0) return false
  return matchedTerms.length / contentTokens.length >= 0.3 || matchedTerms.length >= 3
}

function buildCharacterLockInstructions(query: string): string {
  if (isTheologicalQuery(query)) {
    return ''
  }

  return `
===== OFF-TOPIC DETECTION (STRICT) =====
The user's message is NOT about theology, religion, or Islamic discussion. As the Islam Preacher AI:
1. You are NOT interested in this topic
2. Do NOT answer their question
3. Do NOT engage with their topic
4. Respond with ONE short sentence redirecting to theological discussion — then STOP
5. Exact patterns to use (pick one, no elaboration):
   - "That's not what we're discussing. Do you have a question about God?"
   - "I'm here to discuss theological matters. Is there something about Islam you want to know?"
   - "I'm not interested in that. Let's talk about what matters — the Truth."
   - "If you want to discuss theology, I'm here. Otherwise, I'm not your person."
6. Then say NOTHING else. Do not explain. Do not repeat. Single sentence only.`
}

// Extract a concise topic anchor from the user query for thread tracking
function extractThreadAnchor(query: string): string {
  const lower = query.toLowerCase().trim()

  // Common question patterns — extract the core ask
  const questionPatterns = [
    /^(?:why|how|what|where|when|who|can|could|does|is|are|do|did)\s+(.+?)\s*\??$/i,
    /^(?:tell me about|explain|define|describe)\s+(.+?)$/i,
    /^(?:what (?:do|does|did) (?:you|muslims?|islam|the quran))\s+(.+?)$/i,
    /^(?:is it true that|is islam)\s+(.+?)$/i,
  ]

  for (const pattern of questionPatterns) {
    const match = query.match(pattern)
    if (match && match[1]) {
      const topic = match[1].substring(0, 120)
      return topic.charAt(0).toUpperCase() + topic.slice(1)
    }
  }

  // Fallback: take first 8 significant words
  const tokens = lower.replace(/[^a-z0-9\s]/g, ' ').split(/\s+/).filter(t => t.length > 2)
  const stopwords = new Set(['the', 'this', 'that', 'with', 'from', 'what', 'when',
    'where', 'which', 'about', 'would', 'could', 'should', 'their', 'there',
    'tell', 'have', 'has', 'had', 'does', 'doesnt', 'dont', 'wont',
  ])
  const significant = tokens.filter(t => !stopwords.has(t)).slice(0, 8)
  if (significant.length >= 3) {
    return significant.join(' ')
  }

  // Last resort: first 60 chars
  return query.substring(0, 60).replace(/[?.,!]/g, '').trim()
}

interface PipelineOptions {
  userName?: string
  userReligion?: string
  userBelief?: string
  mode?: 'learning' | 'debating'
}

export async function runContextPipeline(
  userQuery: string,
  onProgress?: ProgressCallback,
  opts?: PipelineOptions
): Promise<ContextPipelineResult> {
  const errors: string[] = []
  logger.info('Running context pipeline', {
    queryLength: userQuery.length,
    queryPreview: userQuery.slice(0, 80),
    mode: opts?.mode,
    hasUser: !!(opts?.userName),
  })

  // Phase 1: Load Persona
  onProgress?.({ phase: 'persona', message: 'Loading scholar persona...' })
  let personaPrompt = buildSystemPrompt({ userName: opts?.userName, userReligion: opts?.userReligion, userBelief: opts?.userBelief })
  logger.debug('Persona prompt built', { promptLength: personaPrompt.length })

  // Override behavior based on selected mode
  const adjustments: string[] = []
  if (opts?.mode === 'debating') {
    adjustments.push(`DEBATE MODE SELECTED by user. SKIP PHASE 1 (OPENING) and PHASE 2 (INTENT CLASSIFICATION) entirely.`)
    if (opts?.userBelief) {
      adjustments.push(`You already know user's belief: ${opts.userBelief}. SKIP PHASE 3 (BELIEF ASCERTAINMENT) as well. Do NOT ask "What do you believe?" Proceed directly to debate.`)
    } else {
      adjustments.push(`After greeting, immediately ask about their beliefs (PHASE 3). Do NOT ask about intent.`)
    }
    adjustments.push(`Be aggressive, logical, confrontational. Engage in debate immediately.`)
  } else if (opts?.mode === 'learning') {
    adjustments.push(`LEARNING MODE SELECTED. After greeting, warmly welcome questions. Be patient and educational. Do NOT ask about intent — they came to learn.`)
  }
  if (adjustments.length > 0) {
    personaPrompt = adjustments.join('\n\n') + '\n\n' + personaPrompt
  }

  // Phase 2: Search closest debate transcripts
  onProgress?.({ phase: 'transcripts', message: 'Searching knowledge base for closest matches...' })
  let transcriptContext = ''
  let transcriptResults: { chunk: TranscriptChunk; score: number }[] = []
  try {
    transcriptResults = await searchTranscripts(userQuery, 5)
    logger.info('Transcript search completed', {
      resultCount: transcriptResults.length,
      topScore: transcriptResults[0]?.score || 0,
    })
    if (transcriptResults.length > 0) {
      transcriptContext = '\n\n[RELEVANT DEBATE TRANSCRIPTS]:\n\n'
      for (const { chunk, score } of transcriptResults) {
        transcriptContext += `--- From: ${chunk.title || chunk.source} (relevance: ${score})\n`
        transcriptContext += `${chunk.preview}...\n\n`
      }
    }
  } catch (err) {
    const msg = `Transcript search unavailable: ${err instanceof Error ? err.message : 'unknown error'}`
    logger.warn('Transcript search failed', { error: msg })
    errors.push(msg)
    onProgress?.({ phase: 'error', message: msg, error: msg })
  }

  // Phase 3: Query Vector DB (if accessible — opt-in, graceful fallback)
  onProgress?.({ phase: 'vector-db', message: 'Querying vector database for scripture references...' })
  let vectorContext = ''
  try {
    vectorContext = await queryVectorDb(userQuery)
    logger.debug('Vector DB query completed', {
      contextLength: vectorContext.length,
      hasResults: vectorContext.length > 0,
    })
  } catch (err) {
    const msg = `Vector DB unavailable: ${err instanceof Error ? err.message : 'unknown error'}`
    logger.warn('Vector DB query failed', { error: msg })
    onProgress?.({ phase: 'error', message: msg, error: msg })
  }

  // Assemble combined system prompt
  let combinedSystemPrompt = personaPrompt

  // Add thread anchor — reminds the AI of the core topic so it can return after tangents
  const threadAnchor = extractThreadAnchor(userQuery)
  if (threadAnchor) {
    combinedSystemPrompt += `\n\n===== TOPIC ANCHOR (RETURN HERE AFTER TANGENTS) =====\nThe user's main topic is: ${threadAnchor}\n- You may use examples, analogies, and tangents to explain your point\n- But you MUST explicitly return to this topic after each tangent\n- Use phrases like "The point I'm making is...", "Coming back full circle...", "So to answer your question about ${threadAnchor}..."\n- NEVER end a response on a tangent. Always bring it back to the main topic.`
  }

  if (transcriptContext) {
    combinedSystemPrompt += `\n\n---\n${transcriptContext}`
  }

  if (vectorContext) {
    combinedSystemPrompt += `\n\n---\n${vectorContext}`
  }

  // Add character-lock enforcement
  const lockInstructions = buildCharacterLockInstructions(userQuery)
  if (lockInstructions) {
    combinedSystemPrompt += lockInstructions
  }

  if (errors.length > 0) {
    onProgress?.({ phase: 'complete', message: `Context ready (${errors.length} non-critical warnings)` })
  } else {
    onProgress?.({ phase: 'complete', message: 'All context sources loaded — generating response...' })
  }

  logger.info('Context pipeline complete', {
    errors: errors.length,
    transcriptChunks: transcriptResults.length,
    hasVectorContext: vectorContext.length > 0,
    combinedPromptLength: combinedSystemPrompt.length,
  })

  const uniqueSources = [...new Set(transcriptResults.map(r => r.chunk.source || r.chunk.title).filter(Boolean))]

  return {
    personaPrompt,
    transcriptContext,
    transcriptResults,
    vectorContext,
    combinedSystemPrompt,
    threadAnchor,
    pipelineInfo: {
      transcriptChunks: transcriptResults.length,
      transcriptSources: uniqueSources,
      vectorDbUsed: vectorContext.length > 0,
      vectorDbResults: vectorContext ? (vectorContext.match(/--- /g) || []).length : 0,
    },
  }
}

export { isTheologicalQuery }
